/* eslint-disable no-fallthrough */
import axios, { AxiosResponse } from "axios";
import { DataLayerObject } from "@gtm-support/core";
import { EngagementService } from "@Api/EngagementService";
import { ContentService } from "@Api/ContentService";
import { CookieManager } from "@Utilities/CookieManager";
import { DataPrivacyPolicy } from "@Utilities/DataPrivacyPolicy";

//import { IBackInStockNotificationData } from "@Api/BackInStockReminderService";
import { TagManager } from "@ThirdParty/TagManager";

declare global
{
	interface Window
	{
		__attnLoaded: true;
		__attentive: {
			/**
			 * See documentation: https://help.attentivemobile.com/hc/en-us/articles/10913611055636-Set-up-Attentive-s-sign-up-unit-trigger
			 * @param { string } filter - leave null
			 * @param { string } client_id - leave null
			 * @param { string } email - leave null
			 * @param { string } creative_id: the creative id to be displayed. obtained from the Attentive dashboard
			 */
			trigger(filter: string, client_id: string, email: string, creative_id: string);
		};
	}
}

interface ISubscriptionData
{
	email: string;
	name?: string;
	optOut?: boolean;
	smsSubscribed?: boolean;
	subscriptionId: string;
}

interface IIdentifyData
{
	phone: string;
	email: string;
	externalIdentifiers: {
		name: string;
		value: string;
	};
}

export class EngagementProvider
{
	private readonly dataPrivacyPolicy: DataPrivacyPolicy;
	private readonly engagementService: EngagementService;
	private AttentiveOverlayID = "attentive_overlay";
	private BodyObserver: MutationObserver;
	private StyleObserver: MutationObserver;
	private Currency = "currency";
	private ItemQuantityList = "ecomm_quantity";
	private ItemSkuList = "ecomm_prodsku";
	private OrderGuidKey = "ogid";
	private ProductId = "ecomm_prodid";
	private ProductThumbList = "ecomm_productThumbList";
	private ProductTitle = "ecomm_productTitle";
	private ProductValue = "ecomm_pvalue";
	private CartCookie = "CART_v1";
	private VisitorCookie = "VISITOR_v1";

	constructor()
	{
		this.dataPrivacyPolicy = new DataPrivacyPolicy();
		this.engagementService = new EngagementService();
	}

	//This happens when a user clicks a link in an email. Then we can tie the brower session to a known account.
	public AddRecommendationClick = (unique: string): void =>
	{
		this.engagementService.AddRecommendationClick(unique);
	};

	public AddSubscription = (phone: string, email: string, subscriptionId: string): void =>
	{
		this.engagementService.AddSubscription(phone, email, subscriptionId);
	};

	public BodyCallback: MutationCallback = (mutationList, observer): void =>
	{
		mutationList.forEach(mutation =>
		{
			if (mutation.type === "childList" && mutation.addedNodes.length > 0)
			{
				const attentiveOverlay = document.getElementById(this.AttentiveOverlayID);

				if (attentiveOverlay)
				{
					attentiveOverlay.style.zIndex = "";
					observer.disconnect();

					this.StyleObserver = new MutationObserver(this.StyleCallback);
					this.StyleObserver.observe(attentiveOverlay, {
						attributes: true,
						attributeFilter: ["style"]
					});
				}
				mutation.addedNodes.forEach(node =>
				{
					const element = node as HTMLElement;

					if (element.id === this.AttentiveOverlayID)
					{
						observer.disconnect();
						element.style.zIndex = "";
					}
				});
			}
		});
	};

	public CreateGuestAccount = (email: string, phone = "", attentiveEmail = false, attentiveSms = false): Promise<any> =>
	{
		return this.engagementService.AddGuestAccount(email, phone, attentiveEmail, attentiveSms)
			.catch((error) =>
			{
				console.error(error);
			});
	};

	public PostAddToCartTagToAttentive = (): void =>
	{
		this.engagementService.GetAccountEmailPhone()
			.then((response: AxiosResponse) =>
			{
				const email: string = response.data.Email;
				const phone: string = response.data.Phone;

				if (typeof window.dataLayer !== "undefined" && typeof window.attentive !== "undefined")
				{
					const currency = window.dataLayer.find((obj: DataLayerObject) => Object.prototype.hasOwnProperty.call(obj, this.Currency))[this.Currency];
					const itemSku = window.dataLayer.find((obj: DataLayerObject) => Object.prototype.hasOwnProperty.call(obj, this.ItemSkuList))[this.ItemSkuList];
					const itemQuantity = window.dataLayer.find((obj: DataLayerObject) => Object.prototype.hasOwnProperty.call(obj, this.ItemQuantityList))[this.ItemQuantityList];
					const productId = window.dataLayer.find((obj: DataLayerObject) => Object.prototype.hasOwnProperty.call(obj, this.ProductId))[this.ProductId];
					const productTitle = window.dataLayer.find((obj: DataLayerObject) => Object.prototype.hasOwnProperty.call(obj, this.ProductTitle))[this.ProductTitle];
					const productThumbList = window.dataLayer.find((obj: DataLayerObject) => Object.prototype.hasOwnProperty.call(obj, this.ProductThumbList))[this.ProductThumbList];
					const productValue = window.dataLayer.find((obj: DataLayerObject) => Object.prototype.hasOwnProperty.call(obj, this.ProductValue))[this.ProductValue];
					const orderGuid = this.GetOrderGuid();

					if (productId && productTitle && productThumbList && productValue && currency)
					{
						var product =
						{
							items:
								[
									{
										productId: productId,
										productVariantId: itemSku,
										name: productTitle,
										productImage: productThumbList,
										price:
										{
											value: productValue,
											currency: currency
										},
										quantity: itemQuantity
									}
								],
							cart:
							{
								cartId: orderGuid
							},
							user:
							{
								phone: phone,
								email: email
							}
						};

						window.attentive.analytics.addToCart(product);
					}
				}
			})
			.catch((error) =>
			{
				console.log(error);
			});;
	};

	public PostProductTagToAttentive = (): void =>
	{
		this.engagementService.GetAccountEmailPhone()
			.then((response: AxiosResponse) =>
			{
				const email: string = response.data.Email;
				const phone: string = response.data.Phone;

				if (typeof window.dataLayer !== "undefined" && typeof window.attentive !== "undefined")
				{
					const productId = window.dataLayer.find((obj: DataLayerObject) => Object.prototype.hasOwnProperty.call(obj, this.ProductId))[this.ProductId];
					const productTitle = window.dataLayer.find((obj: DataLayerObject) => Object.prototype.hasOwnProperty.call(obj, this.ProductTitle))[this.ProductTitle];
					const productThumbList = window.dataLayer.find((obj: DataLayerObject) => Object.prototype.hasOwnProperty.call(obj, this.ProductThumbList))[this.ProductThumbList];
					const productValue = window.dataLayer.find((obj: DataLayerObject) => Object.prototype.hasOwnProperty.call(obj, this.ProductValue))[this.ProductValue];
					const currency = window.dataLayer.find((obj: DataLayerObject) => Object.prototype.hasOwnProperty.call(obj, this.Currency))[this.Currency];

					if (productId && productTitle && productThumbList && productValue && currency)
					{
						var product =
						{
							items:
								[
									{
										productId: productId,
										name: productTitle,
										productImage: productThumbList,
										price:
										{
											value: productValue,
											currency: currency
										}
									}
								],
							user:
							{
								phone: phone,
								email: email
							}
						};

						window.attentive.analytics.productView(product);
					}
				}
			})
			.catch((error) =>
			{
				console.log(error);
			});;
	};

	/**
 * Displays an Attentive sign up modal
 * @param creativeID The creative ID you want to display. Obtained from the Attentive dashboard
 */
	public DisplaySignup = (creativeID: string): void =>
	{
		if (typeof window.__attentive !== "undefined")
		{
			// Documentation: https://docs.attentivemobile.com/pages/developer-guides/tag-management/set-up-attentive-signup-unit-trigger/
			window.__attentive.trigger("null", "null", "null", creativeID);
		}
	};

	public DownloadSdk = (): void =>
	{
		TagManager.CallEvent(TagManager.Events.AttentiveDownload);
	};

	public FixOverlay = (): void =>
	{
		const body = document.querySelector("body");

		this.BodyObserver = new MutationObserver(this.BodyCallback);
		this.BodyObserver.observe(body, {
			childList: true,
			subtree: false
		});
	};

	public HandleEmailCapture = (e: Event): void =>
	{
		const input = e.currentTarget as HTMLInputElement;
		const emailAddress = input.value.toString();

		if (emailAddress !== "")
		{
			this.CreateGuestAccount(emailAddress);
		}
	};

	public StyleCallback: MutationCallback = (mutationList, observer): void =>
	{
		mutationList.forEach(mutation =>
		{
			if (mutation.type === "attributes")
			{
				const element = mutation.target as HTMLElement;
				element.style.zIndex = "";
			}
		});
	};

	public SetModalEligibility = (): void =>
	{
		if (typeof ContentService !== "undefined" && typeof ContentService.Response !== "undefined")
		{
			const footerExists = document.getElementById("footer-subscribe");
			const couponCookie = CookieManager.Cookies.Coupon;
			const isPromotion = ContentService.Response.CouponIsPromotion;
			const smsModalEligibleCookie = CookieManager.Cookies.SmsModalEligible;
			const smsWelcomeCouponCookie = CookieManager.Cookies.SmsWelcomeCoupon;
			const welcomeCouponCookie = CookieManager.Cookies.WelcomeCoupon;
			const validCoupon = ContentService.Response.CouponIsValid;

			if (footerExists && (!validCoupon || isPromotion))
			{
				//Set modal eligibility if the footer exists AND either the coupon isn't valid or is a sitewide promo.
				CookieManager.SetCookie(smsModalEligibleCookie.Key, "true", 365);
				const welcomeCouponValue = ContentService.Response.WelcomeCoupon;
				CookieManager.SetCookie(smsWelcomeCouponCookie.Key, welcomeCouponValue, 365);
			}
			else
			{
				CookieManager.SetCookie(smsModalEligibleCookie.Key, "false", 365);
				CookieManager.SetCookie(smsWelcomeCouponCookie.Key, "", 365);
			}
		}
	};

	/**
	* This is the callback that Attentive will fire when one of their hosted popups are interacted with. We have to associate their data with Listrak
	*/
	public SmsCallback = (data: any): void =>
	{
		if (data)
		{
			const email = data.email;
			const phone = data.phone;
			const smsSubscribed = (typeof phone !== "undefined");

			this.CreateGuestAccount(email, phone, true, smsSubscribed);

			//This function gets called twice by Attentive because it's a 2 step modal and it's possible
			//the user only completes the first step. The email gets saved on step 1 and the phone
			//gets saved on step 2.
			if (!smsSubscribed)
			{
				TagManager.CallEvent(TagManager.Events.EmailModalSignup);
			}
			else
			{
				TagManager.CallEvent(TagManager.Events.SmsModalSignup);
			}

			CookieManager.SetCookie("seenModal", "true", 365);
		}
	};

	public TriggerContentCallbacks = (): void =>
	{
		this.SetModalEligibility();
		this.FixOverlay();
		this.ConfigureEmailCapture();
	};

	// Configure Email Capture on all email type inputs
	private ConfigureEmailCapture = (): void =>
	{
		if (this.dataPrivacyPolicy.MarketingApproved() === true)
		{
			const emailInputs = document.querySelectorAll<HTMLInputElement>("input[type='email']");

			if (emailInputs.length > 0)
			{
				emailInputs.forEach((emailInput: HTMLInputElement) =>
				{
					emailInput.addEventListener("blur", this.HandleEmailCapture);
				});
			}
		}
	};

	private GetOrderGuid = (): string =>
	{
		let orderGuid = "";
		const cartCookie = CookieManager.GetCookie(this.CartCookie, this.OrderGuidKey);
		var visitorCookie = CookieManager.GetCookie(this.VisitorCookie, this.OrderGuidKey);

		if (cartCookie)
		{
			orderGuid = cartCookie;
		}
		else if (visitorCookie)
		{
			orderGuid = visitorCookie;
		}

		return orderGuid;
	};
}